import { createMessageGetter } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import deepEquals from '../../../../../Lib/Object/deepEquals';
import { Field, Message, NumericInput, TextOutput } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { Grid, GridFieldSection, ListFieldSection, PageSection } from '../../../../Layout';
import { WorksheetAssociatedTestSection } from '../../../Core';
import externalTestMethods from '../../../ExternalTestMethods';

let OversizeDeterminationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const importedOversizeId = useSelector(
    createWorksheetSelector!('Preparation.AssociatedOversizeDetermination.ImportedTest.Id')
  ) as Guid;

  const oversizeDeterminationResultsPath = hasValue(importedOversizeId)
    ? 'OversizeDeterminationResults'
    : 'AssociatedOversizeDetermination.ExternalTest.Results';

  const allSieves = useSelector(
    createWorksheetSelector!(`Preparation.${oversizeDeterminationResultsPath}.AllSieves`),
    deepEquals
  );

  const importedOversizeDeterminationMessage = useSelector(
    pipe(createWorksheetSelector!('Messages'), createMessageGetter('AssociatedOversizeDetermination')),
    deepEquals
  );

  const getRowHeaders = () => {
    if (!allSieves) return [];
    return allSieves.map((s: any) => s.Caption);
  };

  const getExternalGridData = () => {
    if (!allSieves) return [];
    return allSieves.map((_sieveDef: any, index: any) => [
      <Field
        input={<NumericInput />}
        name={`Preparation.AssociatedOversizeDetermination.ExternalTest.Results.Sieves[${index}].CumulativeDryMassRetainedPercent`}
        label="Cumulative Dry Mass Retained (%)"
      />,
    ]);
  };

  const getImportedGridData = () => {
    if (!allSieves) return [];
    return allSieves.map((_: any, index: any) => [
      <Field
        input={<TextOutput />}
        name={`Preparation.OversizeDeterminationResults.Sieves[${index}].CumulativeDryMassRetainedPercent`}
        label="Cumulative Dry Mass Retained (%)"
        blankReplacement="N/A"
      />,
    ]);
  };

  return (
    <PageSection heading="Oversize Determination">
      <ListFieldSection>
        <Field
          input={<NumericInput />}
          label="Correct Results When Oversize is Greater than (%)"
          name={`Preparation.MinimumOversizeForCorrection`}
        />
      </ListFieldSection>
      <WorksheetAssociatedTestSection
        label="Oversize proportion determined by:"
        name="Preparation.AssociatedOversizeDetermination"
        testType="Oversize Determination"
        associatedTestModel={{
          measured: true,
          imported: {
            importedTestResults: (
              <>
                <ListFieldSection>
                  <Message info severity={importedOversizeDeterminationMessage?.MessageType} border={false}>
                    {importedOversizeDeterminationMessage?.Text}
                  </Message>
                </ListFieldSection>
                <GridFieldSection>
                  <Grid
                    cornerHeader="Sieve Size"
                    colHeaders={['Cumulative Dry Mass Retained (%)']}
                    colWidths={130}
                    rowHeaders={getRowHeaders()}
                    rowHeaderWidth={110}
                    data={getImportedGridData()}
                  />
                </GridFieldSection>
              </>
            ),
            importModel: {
              Id: importedOversizeId,
            },
          },
          external: {
            permitOtherTestMethods: true,
            testMethods: [externalTestMethods.psdSieveAnalysis.AASHTO_T_27],
            externalTestResults: (
              <GridFieldSection>
                <Grid
                  cornerHeader="Sieve Size"
                  colHeaders={['Cumulative Dry Mass Retained (%)']}
                  colWidths={130}
                  rowHeaders={getRowHeaders()}
                  rowHeaderWidth={110}
                  data={getExternalGridData()}
                />
              </GridFieldSection>
            ),
          },
        }}
      />
      <ListFieldSection heading="Material Proportions">
        <Field
          input={<TextOutput />}
          label="Oversize Sieve Size"
          name="Preparation.ScreeningASTM.OversizeSieve.Caption"
        />
        <Field input={<TextOutput />} label="Oversize (%)" name="Preparation.ScreeningASTM.OversizePercent" />
      </ListFieldSection>
    </PageSection>
  );
};

OversizeDeterminationSection = withInjectedProps(OversizeDeterminationSection);

export { OversizeDeterminationSection };
export default OversizeDeterminationSection;
